import { Badge, Button, Switch, message } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import {
  getProfileReasons,
  updateProfileReasons,
} from "../../../core/data/redux/employeesSlice";
import { all_routes } from "../../router/all_routes";

// Employee Avatar Component
const EmployeeAvatar = ({ empName, empImage, empId }: { empName: string, empImage?: string, empId: string }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const displayName = empName && empName !== 'N/A' && empName.trim() ? empName : 'Unknown Employee';
  const initials = displayName !== 'Unknown Employee'
    ? displayName.split(' ')
      .map(n => n && n[0] ? n[0] : '')
      .join('')
      .substring(0, 2)
      .toUpperCase()
    : 'UE';

  const showInitials = !empImage || imageError || !imageLoaded;

  // Convert empId to integer for display
  const numericEmpId = parseInt(empId, 10);
  const displayEmpId = isNaN(numericEmpId) ? empId : numericEmpId.toString();

  // Consistent avatar styles
  const avatarStyles = {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0
  };

  const imageStyles = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    borderRadius: '50%'
  };

  const initialsStyles = {
    ...avatarStyles,
    backgroundColor: '#0d6efd',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center' as const,
    lineHeight: '1'
  };

  return (
    <div className="d-flex align-items-center gap-3">
      <div style={avatarStyles}>
        {empImage && !imageError ? (
          <>
            <img
              src={`data:image/jpeg;base64,${empImage}`}
              alt={`${displayName}'s profile`}
              style={{
                ...imageStyles,
                display: showInitials ? 'none' : 'block'
              }}
              onError={() => {
                console.log('Image failed to load for employee:', empId);
                setImageError(true);
              }}
              onLoad={() => {
                console.log('Image loaded successfully for employee:', empId);
                setImageLoaded(true);
              }}
            />
            {showInitials && (
              <div style={initialsStyles}>
                {initials}
              </div>
            )}
          </>
        ) : (
          <div style={initialsStyles}>
            {initials}
          </div>
        )}
      </div>
      <div className="flex-grow-1">
        <div className="text-dark fw-medium mb-1">
          {displayName}
        </div>
        {empId && (
          <div className="text-muted small">
            ID: {displayEmpId}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileReasons = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const employeesState = useSelector((state: any) => state.employeesState);
  const profileReasonsData = employeesState?.data?.data?.result || [];
  const totalRecords = employeesState?.data?.data?.total || 0;

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const fetchProfileReasons = (page = 1, limit = 10) => {
    const payload = {
      page,
      limit,
      orderBy: "DESC",
      orderColumn: "id",
    };
    dispatch(getProfileReasons(payload));
  };

  const handleStatusUpdate = async (record: any) => {
    console.log("handleStatusUpdate called with record:", record);

    if (record.status === "completed") {
      message.warning(
        "This verification is already completed and cannot be changed."
      );
      return;
    }

    // Test direct update first (bypass modal for debugging)
    try {
      console.log("Starting direct update for record:", record);
      setUpdatingStatus(record.id);

      const updatePayload = {
        id: record.id,
        status: "completed",
      };
      console.log("Update payload:", updatePayload);

      const result = await dispatch(
        updateProfileReasons(updatePayload)
      ).unwrap();
      console.log("Update result:", result);

      message.success("Verification status updated successfully!");
    } catch (error: any) {
      console.error("Error updating status:", error);
      message.error(
        error.message || error || "Failed to update verification status"
      );
    } finally {
      setUpdatingStatus(null);
    }
  };

  useEffect(() => {
    fetchProfileReasons();
  }, []);

  useEffect(() => {
    if (
      employeesState?.action === "profile-reasons" &&
      employeesState?.loading === "fulfilled"
    ) {
      setPagination((prev) => ({
        ...prev,
        total: totalRecords,
      }));
    }
    // Handle update verification status success
    else if (
      employeesState?.action === "update-profile-reasons" && employeesState?.loading === "fulfilled") {
      console.log(
        "Verification status updated successfully, refreshing data..."
      );
      // Refresh the profile reasons data
      fetchProfileReasons(pagination.current, pagination.pageSize);
    }

    else if (employeesState?.loading === "rejected") {
      message.error(employeesState?.error || "Operation failed");
    }
  }, [employeesState, totalRecords]);



  // const handleTableChange = (paginationData: any) => {
  //   const newPagination = {
  //     current: paginationData.page,
  //     pageSize: paginationData.pageSize,
  //     total: paginationData.total || totalRecords,
  //   };
  //   setPagination(newPagination);
  //   fetchProfileReasons(paginationData.page, paginationData.pageSize);
  // };

  const handleTableChange = (newPagination: any) => {
    setPagination((prev) => {
      const isPageSizeChanged = prev.pageSize !== newPagination.pageSize;
      const updatedPagination = {
        current: isPageSizeChanged ? 1 : newPagination.current || 1,
        pageSize: newPagination.pageSize,
        total: newPagination.total || prev.total,
      };
      fetchProfileReasons(updatedPagination.current, updatedPagination.pageSize);
      return updatedPagination;
    });
  };


  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; text: string } } = {
      pending: { color: "warning", text: "Pending" },
      completed: { color: "success", text: "Completed" },
    };

    const config = statusConfig[status?.toLowerCase()] || {
      color: "default",
      text: status || "Unknown",
    };
    return <Badge status={config.color as any} text={config.text} />;
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (text: string, record: any, index: number) => (
        <div>
          {pagination.current
            ? index + (pagination.current - 1) * pagination.pageSize + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Employee ID",
      dataIndex: "empId",
      width: 120,
      sorter: true,
      render: (empId: string) => {
        // Convert string to integer, removing leading zeros
        const numericId = parseInt(empId, 10);
        return isNaN(numericId) ? "N/A" : numericId.toString();
      },
    },
    {
      title: "Employee Name",
      dataIndex: "empName",
      width: 200,
      sorter: true,
      render: (empName: string, record: any) => (
        <EmployeeAvatar
          empName={empName}
          empImage={record.empImage}
          empId={record.empId}
        />
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (reason: string) => (
        <div style={{ maxWidth: 300 }}>
          {reason
            ? reason.length > 50
              ? `${reason.substring(0, 50)}...`
              : reason
            : "N/A"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status: string, record: any) => {
        console.log(
          "Rendering status column for record:",
          record,
          "status:",
          status
        );
        return (
          <div className="d-flex align-items-center gap-2">
            {getStatusBadge(status || "pending")}
            <Switch
              size="small"
              checked={status === "completed"}
              disabled={status === "completed" || updatingStatus === record.id}
              loading={updatingStatus === record.id}
              onChange={(checked) => {
                console.log("Switch changed:", checked, "for record:", record);
                if (checked) {
                  handleStatusUpdate(record);
                }
              }}
              checkedChildren="✓"
              unCheckedChildren="○"
            />
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      width: 140,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY hh:mm A") : "N/A",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Profile Reasons</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Employees</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Profile Reasons
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Results Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                  <h4 className="mb-4 mt-3">
                    Profile Verification Reasons ({totalRecords} Records)
                  </h4>
                </div>
                <div className="card-body p-0 py-3">
                  {employeesState.loading === "pending" ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Loading profile reasons...</p>
                    </div>
                  ) : employeesState.error ? (
                    <div className="text-center py-5">
                      <i
                        className="ti ti-alert-circle"
                        style={{ fontSize: "3rem", color: "#dc3545" }}
                      ></i>
                      <p className="text-danger mt-3">Error loading data</p>
                      <p className="text-muted">{employeesState.error}</p>
                      <Button
                        type="primary"
                        onClick={() => fetchProfileReasons()}
                        className="mt-2"
                      >
                        Retry
                      </Button>
                    </div>
                  ) : profileReasonsData.length === 0 ? (
                    <div className="text-center py-5">
                      <i
                        className="ti ti-clipboard-off"
                        style={{ fontSize: "3rem", color: "#ccc" }}
                      ></i>
                      <p className="text-muted mt-3">
                        No profile reasons found.
                      </p>
                      <p className="text-muted">
                        Click refresh to reload data from the server.
                      </p>
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={profileReasonsData}
                      Selection={false}
                      paginationConfig={pagination}
                      handlePagination={handleTableChange}
                      loading={employeesState.loading === "pending"}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProfileReasons;
